import { ApiError, queryKeys, type RentalCardIntent } from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { NewCardPanel, ReaderPanel, SavedCardPanel } from './rental-charge-panels.js'

interface RentalForPayment {
  id: string
  customerId: string
  billingInterval: string
  feeCents: number
  /** Rate x days/weeks (t1366); absent on callers that don't have it. */
  feeTotalCents?: number
  payments: { kind: string; status: string; amountCents?: number }[]
}

/** The amount to prefill for a fee: what is still owed on a daily/weekly
 *  rental, otherwise one period's fee. */
function defaultFeeAmount(rental: RentalForPayment): string {
  if (rental.billingInterval === 'one_time') return ''
  if (rental.billingInterval === 'daily' || rental.billingInterval === 'weekly') {
    const paid = rental.payments
      .filter((p) => p.kind === 'fee' && p.status === 'succeeded')
      .reduce((sum, p) => sum + (p.amountCents ?? 0), 0)
    const owed = (rental.feeTotalCents ?? rental.feeCents) - paid
    if (owed > 0) return (owed / 100).toFixed(2)
  }
  return (rental.feeCents / 100).toFixed(2)
}

/** A deposit already collected, on hold, or in flight — only one at a time
 *  can exist (t0951); a declined, released, or expired one doesn't count. */
function hasActiveDeposit(payments: { kind: string; status: string }[]): boolean {
  return payments.some(
    (p) => p.kind === 'deposit' && ['pending', 'authorized', 'succeeded'].includes(p.status),
  )
}

type Method = 'cash' | 'external_card' | 'check' | 'zelle' | 'new_card' | 'saved_card' | 'reader'

/**
 * Integrated card charging for rental fees and deposits (t0941). Cash and
 * Card machine still just record a row, the way they always have — the other
 * three methods actually charge a card through Stripe first and only record
 * the payment once that charge succeeds:
 *
 *  - Charge card: a card keyed in here, via Stripe Elements (mirrors
 *    `CardSetupPanel`/`CardSetupForm` in `saved-cards-card.tsx`).
 *  - Saved card: an existing card from the customer's profile, charged
 *    off-session in one call.
 *  - Card reader: a connected Stripe Terminal reader.
 *
 * A newly entered card or a reader opens a PaymentIntent first
 * (`POST .../card-intent`) and locks Kind/Amount/Method while it's live —
 * the charged amount must always match what Stripe is holding. Closing the
 * dialog on an unsettled charge cancels it rather than leaving a stray
 * pending row.
 */
export function RecordPaymentDialog({
  rental,
  open,
  onClose,
}: {
  rental: RentalForPayment
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<'fee' | 'deposit'>('fee')
  const [method, setMethod] = useState<Method>('cash')
  const [amount, setAmount] = useState(defaultFeeAmount(rental))
  const [intent, setIntent] = useState<RentalCardIntent | null>(null)
  const [readerReady, setReaderReady] = useState(false)

  useEffect(() => {
    if (open) {
      setKind('fee')
      setMethod('cash')
      setAmount(defaultFeeAmount(rental))
      setIntent(null)
      setReaderReady(false)
    }
  }, [open, rental.billingInterval, rental.feeCents, rental.feeTotalCents])

  const amountCents = Math.round(Number(amount) * 100)
  const validAmount = amountCents > 0 && !Number.isNaN(amountCents)
  // Once a PaymentIntent is live, the amount it was opened for can never
  // change out from under it.
  const locked = intent !== null
  const depositBlocked = hasActiveDeposit(rental.payments)
  const cardMethod = method === 'new_card' || method === 'saved_card' || method === 'reader'
  const effectiveValidAmount = validAmount && !(kind === 'deposit' && depositBlocked)

  const invalidateAfterCharge = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })

  const abandon = () => {
    if (intent) {
      // Fire-and-forget: the row is only ever a `pending` placeholder at this
      // point, and the worst case of this failing is a row a retry or the
      // webhook settles later — never money charged twice.
      void api
        .post(`/rentals/${rental.id}/payments/${intent.rentalPaymentId}/cancel`, {})
        .catch(() => {})
    }
  }

  const close = () => {
    abandon()
    onClose()
  }

  const record = useMutation({
    mutationFn: () =>
      api.post(`/rentals/${rental.id}/payments`, { kind, provider: method, amountCents }),
    onSuccess: async () => {
      await invalidateAfterCharge()
      toast.success('Payment recorded')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not record the payment',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const startIntent = useMutation({
    mutationFn: () =>
      api.post<RentalCardIntent>(`/rentals/${rental.id}/payments/card-intent`, {
        kind,
        amountCents,
        method: method === 'reader' ? 'reader' : 'card',
      }),
    onSuccess: (result) => setIntent(result),
    onError: (error) =>
      toast.error(
        'Could not start the charge',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const onSettled = async () => {
    await invalidateAfterCharge()
    toast.success(kind === 'deposit' ? 'Deposit hold placed' : 'Payment charged')
    onClose()
  }

  const showContinue = !intent && (method === 'new_card' || (method === 'reader' && readerReady))

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="Kind"
            hint={depositBlocked ? 'A deposit is already collected or on hold.' : undefined}
          >
            {(props) => (
              <Select
                value={kind}
                onValueChange={(v) => setKind(v as 'fee' | 'deposit')}
                disabled={locked}
              >
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fee">Fee</SelectItem>
                  <SelectItem value="deposit" disabled={depositBlocked}>
                    Deposit
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field
            label="Amount"
            required
            hint={
              kind === 'deposit' && cardMethod
                ? 'Card deposits place a hold, not a charge — it expires in about a week (sooner on the reader) if not settled.'
                : undefined
            }
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                step="0.01"
                value={amount}
                disabled={locked}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
          <Field label="How">
            {(props) => (
              <Select
                value={method}
                onValueChange={(v) => setMethod(v as Method)}
                disabled={locked}
              >
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="external_card">Card machine</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="new_card">Charge card</SelectItem>
                  <SelectItem value="saved_card">Saved card</SelectItem>
                  <SelectItem value="reader">Card reader</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>

          {method === 'saved_card' ? (
            <SavedCardPanel
              rentalId={rental.id}
              customerId={rental.customerId}
              kind={kind}
              amountCents={amountCents}
              validAmount={effectiveValidAmount}
              onSettled={onSettled}
            />
          ) : null}

          {method === 'new_card' && intent ? (
            <NewCardPanel intent={intent} rentalId={rental.id} kind={kind} onSettled={onSettled} />
          ) : null}

          {method === 'reader' ? (
            <ReaderPanel
              rentalId={rental.id}
              intent={intent}
              kind={kind}
              onReady={setReaderReady}
              onSettled={onSettled}
            />
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          {method === 'cash' ||
          method === 'external_card' ||
          method === 'check' ||
          method === 'zelle' ? (
            <Button
              variant="primary"
              disabled={!effectiveValidAmount}
              loading={record.isPending}
              onClick={() => record.mutate()}
            >
              Record
            </Button>
          ) : null}
          {showContinue ? (
            <Button
              variant="primary"
              disabled={!effectiveValidAmount}
              loading={startIntent.isPending}
              onClick={() => startIntent.mutate()}
            >
              Continue
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
